import { Component, Input } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  DataUpdateRequestItem,
  ItemStatus,
} from 'src/app/dashboard/interfaces/data-update-request.interface';
import {
  findPhonePrefix,
  formatDateForDisplay,
  isDateFieldKey,
  PHONE_FIELD_KEY,
  PHONE_PREFIX_FIELD_KEY,
  PHONE_PREFIX_OPTIONS,
  PhonePrefixOption,
  sanitizePhoneNumber,
} from '../../data-update-request.utils';

/**
 * Renderiza un item escalar (text/number/date/boolean/email) del form de
 * actualización. Recibe un `FormControl` del padre — todos los controls
 * viven en el FormGroup del componente principal `DataUpdateRequestComponent`.
 *
 * Items con status `APPROVED` se ocultan del componente padre (no se
 * agregan al FormGroup), así que aquí asumimos siempre que el control
 * existe O el item es read-only (raro pero posible).
 */
@Component({
  selector: 'app-update-field-input',
  templateUrl: './update-field-input.component.html',
  styleUrls: ['./update-field-input.component.sass'],
})
export class UpdateFieldInputComponent {
  @Input() item!: DataUpdateRequestItem;
  @Input() label: string = '';
  @Input() control: FormControl | null = null;
  @Input() disabled: boolean = false;

  /**
   * Determina el `type` del `<input>` HTML según el `fieldType` del item.
   */
  get inputType(): string {
    switch (this.item?.fieldType) {
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      case 'email':
        return 'email';
      default:
        return 'text';
    }
  }

  /**
   * `true` si este item debe renderizar el select de países con banderas.
   * Cubre dos casos:
   *   1. Backend expone `fieldType: 'phonePrefix'` explícitamente.
   *   2. Backend expone `fieldType: 'text'` pero el fieldKey es el
   *      canónico del prefijo (`prefijoTelefono`) — compat con backends
   *      que todavía no adopten el nuevo tipo.
   */
  get isPhonePrefixField(): boolean {
    if (this.item?.fieldType === 'phonePrefix') return true;
    return this.item?.fieldKey === PHONE_PREFIX_FIELD_KEY;
  }

  /** Opciones del select — nombre visible + bandera + código. */
  readonly phonePrefixOptions: ReadonlyArray<PhonePrefixOption> = PHONE_PREFIX_OPTIONS;

  /** Opción actualmente seleccionada (para mostrar bandera al lado del select). */
  get selectedPhonePrefix(): PhonePrefixOption | null {
    const code = this.control?.value as string | null | undefined;
    return findPhonePrefix(code);
  }

  /** Path completo al asset de la bandera. */
  flagPath(flag: string): string {
    return `assets/flags/${flag}`;
  }

  /** ¿Este item es el número de teléfono? (activa sanitización numérica) */
  get isPhoneNumberField(): boolean {
    return this.item?.fieldKey === PHONE_FIELD_KEY;
  }

  /**
   * Sanitiza en tiempo real el input del teléfono: sólo permite dígitos,
   * elimina espacios/guiones/signos al vuelo. Preserva la posición del
   * cursor para no interrumpir al usuario mientras escribe.
   */
  onPhoneNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input || !this.control) return;
    const raw = input.value;
    const cleaned = sanitizePhoneNumber(raw);
    if (cleaned === raw) return;
    // Ajustamos posición del cursor descontando los caracteres removidos
    // que estaban antes de la posición actual.
    const selectionEnd = input.selectionEnd ?? cleaned.length;
    const removedBefore =
      raw.slice(0, selectionEnd).replace(/\d/g, '').length;
    input.value = cleaned;
    this.control.setValue(cleaned, { emitEvent: false });
    const newCursor = Math.max(0, selectionEnd - removedBefore);
    try {
      input.setSelectionRange(newCursor, newCursor);
    } catch {
      // Algunos input types (date/number) no permiten setSelectionRange
      // — no crítico, el sanitizado funciona igual.
    }
  }

  /**
   * Read-only cuando el item ya está aprobado, ya enviado y en revisión,
   * cuando el padre nos pasa `disabled = true`, o cuando no hay control
   * en el FormGroup (caso SUBMITTED — el padre intencionalmente NO lo
   * agrega para evitar que el cliente pise un valor en revisión).
   */
  get isReadOnly(): boolean {
    return (
      this.item.status === 'APPROVED' ||
      this.item.status === 'SUBMITTED' ||
      this.disabled ||
      !this.control
    );
  }

  /**
   * Cuando el item está SUBMITTED no hay control y el input no renderiza —
   * en su lugar mostramos el valor que el cliente envió, como texto
   * read-only con un cartel "en revisión".
   */
  get displayValueForReadOnly(): string {
    if (this.item.status === 'SUBMITTED' && this.item.submittedValue != null) {
      return this.formatValue(this.item.submittedValue);
    }
    if (this.item.status === 'APPROVED' && this.item.submittedValue != null) {
      return this.formatValue(this.item.submittedValue);
    }
    if (this.item.currentValue != null && this.item.currentValue !== '') {
      return this.formatValue(this.item.currentValue);
    }
    return '—';
  }

  private formatValue(v: unknown): string {
    if (typeof v === 'boolean') return v ? 'Sí' : 'No';
    // Para prefijos telefónicos, si el valor coincide con un país
    // conocido, mostramos "Chile (+56)" en lugar del código pelado.
    if (this.isPhonePrefixField && typeof v === 'string') {
      const opt = findPhonePrefix(v);
      if (opt) return `${opt.name} (${opt.code})`;
    }
    // Fechas: mostrar "DD/MM/YYYY" en lugar del ISO crudo.
    if (this.isDateField && typeof v === 'string' && v) {
      const formatted = formatDateForDisplay(v);
      if (formatted) return formatted;
    }
    return String(v);
  }

  /**
   * ¿Este item se maneja como fecha? Cubre `fieldType === 'date'` y
   * también fieldKeys canónicos (`fechaNacimiento`, `fechaExp`, …) por
   * si el backend los tipa como `text`.
   */
  get isDateField(): boolean {
    if (this.item?.fieldType === 'date') return true;
    return isDateFieldKey(this.item?.fieldKey ?? '');
  }

  get currentValueLabel(): string | null {
    // Sólo mostramos el "Actual: ..." debajo del input cuando hay un
    // valor previo y el item está editable. Para SUBMITTED/APPROVED el
    // valor ya se muestra en `displayValueForReadOnly`.
    if (this.isReadOnly) return null;
    const cv = this.item.currentValue;
    if (cv === null || cv === undefined || cv === '') return null;
    // Fecha: convertir ISO / "YYYY-MM-DD" a "DD/MM/YYYY" para legibilidad.
    if (this.isDateField && typeof cv === 'string') {
      const formatted = formatDateForDisplay(cv);
      if (formatted) return formatted;
    }
    // Prefijo telefónico: "Chile (+56)" en lugar del código pelado.
    if (this.isPhonePrefixField && typeof cv === 'string') {
      const opt = findPhonePrefix(cv);
      if (opt) return `${opt.name} (${opt.code})`;
    }
    return String(cv);
  }

  get hasRequiredError(): boolean {
    return (
      !!this.control &&
      this.control.touched &&
      this.control.hasError('required')
    );
  }

  get hasEmailError(): boolean {
    return (
      !!this.control && this.control.touched && this.control.hasError('email')
    );
  }

  statusLabel(status: ItemStatus): string {
    switch (status) {
      case 'PENDING':
        return 'Pendiente';
      case 'SUBMITTED':
        return 'En revisión';
      case 'APPROVED':
        return 'Aprobado';
      case 'REJECTED':
        return 'Rechazado';
      default:
        return status;
    }
  }

  statusColor(status: ItemStatus): string {
    switch (status) {
      case 'PENDING':
        return '#f59e0b';
      case 'SUBMITTED':
        return '#3b82f6';
      case 'APPROVED':
        return '#16a34a';
      case 'REJECTED':
        return '#dc2626';
      default:
        return '#6b7280';
    }
  }
}
